//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helper
import { digitFormat } from 'src/helpers/date_helper';
//Types
import type {
	ScheduleParams,
	ScheduleRequestData,
	TimespecTypes,
	TimespecValue,
} from 'src/types/requests/schedules/schedules_g2';

export const parseSchedules = (schedule: ScheduleRequestData): ScheduleParams => {
	let params: ScheduleParams = null;
	//Parsing schedule timespec cron expressions
	//@see https://github.com/mongoose-os-libs/cron
	const s = '\\s+', //white space
		any = '|\\*', // '*' which means "any value".
		sp = '[-,]', //comma splitter and range
		mHour = `(\\d|1\\d|2[0-3])`, //hours, 0-23
		mMinSec = `([1-5]?\\d)`, //seconds,minutes 0-59
		mDate = `([1-9]|1\\d|2\\d|30|31)`, //date of month, 1-31
		mMonth = `([1-9]|1[0-2])`, //month, 1-12
		mDaysOfWeek = '(SUN|MON|TUE|WED|THU|FRI|SAT|[0-6])', //the day of week
		inc = '(?<inc>[+-])', // - or + ,before or after sunrise/sunset
		sunHour = '(?<sunHour>0?\\d|1[0-2])h', //sunrise/sunset hours, (0?)0-12h
		sunMin = '(?<sunMin>0?\\d|[0-5]\\d)[mh]', //sunrise/sunset minutes (0?)0-59m  ----> Using '[mh]' instead of 'm' due to a bug issue -> https://gitlab.allterco.net/Shelly/fw/gen2-embedded-web/-/issues/774
		hour = `${mHour}((${sp}${mHour}){1,23})?${any}`,
		minSec = `${mMinSec}((${sp}${mMinSec}){1,59})?${any}`,
		datesOfMonth = `${mDate}((${sp}${mDate}){1,30})?${any}`, //the date of month or multiple days separated with commas or * any
		months = `${mMonth}((${sp}${mMonth}){1,11})?${any}`, //the month or multiple months separated with commas or * any
		daysOfWeek = `(${mDaysOfWeek}((,${mDaysOfWeek}){1,6})?${any})`, //the day of week
		sunAppears = `(@(?<type>sunrise|sunset)(${inc}${sunHour}(${sunMin})?)?)`, //sunrise/sunset, plus optional offset like +2h05m or -02h00m or +02h
		time = `(?<sec>${minSec})${s}(?<min>${minSec})${s}(?<hour>${hour})`, //seconds minutes hours
		dateSpec = `${s}(?<dates>${datesOfMonth})${s}(?<months>${months})${s}(?<days>${daysOfWeek})`, //dates months daysOfWeek
		mTime = new RegExp(`^(${sunAppears}|${time})${dateSpec}$`),
		m = schedule.timespec.match(mTime);

	if (m) {
		const g = m.groups;
		params = {
			...schedule,
			type: (g.type as 'time') || 'time',
			inc: (g.inc as '+') || '+',
			hour: Number(g.hour) || 0,
			min: Number(g.min) || 0,
			sec: 0, //Number(g.sec) || 0,
			sunHour: Number(g.sunHour) || 0,
			sunMin: Number(g.sunMin) || 0,
			days: parseDaysOfWeek(g.days),
			hours: parseTimespecValue(g.hour, 'hours'),
			minutes: parseTimespecValue(g.min, 'minutes'),
			seconds: parseTimespecValue(g.sec, 'seconds'),
			dates: parseTimespecValue(g.dates, 'dates'),
			months: parseTimespecValue(g.months, 'months'),
		};

		if (
			params.type == 'time' &&
			(params.hours.values.length > 1 ||
				params.minutes.values.length > 1 ||
				params.seconds.values.length > 1 ||
				g.sec != '0')
		) {
			params.hours.selected = g.hour != '0';
			params.minutes.selected = g.min != '0';
			params.type = 'advancedTime';
		}
	}
	if (params == null) {
		console.warn(schedule.timespec, { mTime });
	}
	// console.log({ m }, schedule.timespec, { mTime }, { params });
	return params;
};

export const prepareToSave = (schedule: ScheduleParams): ScheduleRequestData => {
	const dateSpec = `${getTimespecValue(schedule, 'dates')} ${getTimespecValue(
		schedule,
		'months',
	)} ${getDaysOfWeek(schedule.days)}`;
	if (schedule.type == 'time' || schedule.type == 'advancedTime') {
		schedule.timespec = `${
			schedule.type == 'time' ? schedule.sec : getTimespecValue(schedule, 'seconds')
		} ${getTimespecValue(schedule, 'minutes')} ${getTimespecValue(schedule, 'hours')} ${dateSpec}`;
	} else {
		schedule.timespec = `@${schedule.type}`;
		if (schedule.sunHour != 0 || schedule.sunMin != 0) {
			schedule.timespec += `${schedule.inc}${schedule.sunHour}h${digitFormat(schedule.sunMin)}m`;
		}
		schedule.timespec += ` ${dateSpec}`;
	}
	return {
		id: schedule.id,
		enable: !!schedule?.enable,
		timespec: schedule.timespec,
		calls: schedule.calls,
	};
};

export const getWizardItems = () => {
	const $format = get(format),
		wizardStep = [
			{
				title: $format('schedules_wizard_items_week_days', {
					default: 'Week days',
				}),
				subtitle: $format('choose_weekday', { default: 'Choose weekday' }),
			},
			{
				title: $format('schedules_wizard_items_time', { default: 'Time' }),
				subtitle: $format('choose_time_interval', {
					default: 'Choose time interval',
				}),
			},
			{
				title: $format('schedules_wizard_items_action', { default: 'Action' }),
				subtitle: $format('select_action', { default: 'Select action' }),
			},
			{
				title: $format('schedules_wizard_items_preview', {
					default: 'Preview',
				}),
			},
		];
	return wizardStep.map((item, i) => {
		return {
			...item,
			active: i === 0 ? true : false,
			stepNumber: i + 1,
			activeIndex: i,
		};
	});
};

const parseDaysOfWeek = (days: string): number[] => {
	const daysIndexes = {
		SUN: 0,
		MON: 1,
		TUE: 2,
		WED: 3,
		THU: 4,
		FRI: 5,
		SAT: 6,
	};
	if (!days || days == '*') {
		return Object.values(daysIndexes);
	}
	const daysOfWeek = [];
	days.split(',').forEach((d) => daysOfWeek.push(daysIndexes[d] != null ? daysIndexes[d] : Number(d)));
	return daysOfWeek;
};

const getDaysOfWeek = (days: number[]): string => {
	const daysIndexes = {
			0: 'SUN',
			1: 'MON',
			2: 'TUE',
			3: 'WED',
			4: 'THU',
			5: 'FRI',
			6: 'SAT',
		},
		daysOfWeek = [];
	days.forEach((d) => daysOfWeek.push(daysIndexes[d]));
	return daysOfWeek.join(',');
};

const parseTimespecValue = (value: string, type: TimespecTypes): TimespecValue => {
	const any = value == null || value == '*';
	let length = 31;
	let startValue = 1;
	if (type == 'months') {
		length = 12;
	} else if (type == 'hours') {
		length = 24;
		startValue = 0;
	} else if (type != 'dates') {
		length = 60;
		startValue = 0;
	}

	const values = any ? [] : value.split(',');
	const selected =
		(/^(months|dates)$/.test(type) && !any) ||
		(/(^hours|minutes)$/.test(type) && values.length > 1) ||
		(type == 'seconds' && value != '0');
	return {
		any,
		selected,
		startValue,
		length,
		values,
	};
};

const getTimespecValue = (schedule: ScheduleParams, type: TimespecTypes): string => {
	if (schedule.type == 'time' && /^(hours|minutes)$/.test(type)) {
		return type == 'hours' ? schedule.hour.toString() : schedule.min.toString();
	}
	const values = schedule[type].values;
	if (values.length > 0 && schedule[type].selected) {
		return values.join(',');
	}
	return /^(hours|minutes|seconds)$/.test(type) ? '0' : '*';
};
